/**
 * ViewerHome - landing for client_viewer users.
 *
 * If the viewer has access to exactly ONE client, we skip the cruscotto entirely
 * and redirect straight to that client's detail page (Panoramica tab is the
 * default activeTab there). The user requested the existing "Panoramica" view
 * as the home, mobile-optimized — so we land there directly.
 *
 * For viewers with multiple clients, show a compact picker list.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowRight, AlertTriangle, LogOut, Building2 } from "lucide-react";

export default function ViewerHome({ user, onLogout }) {
  const navigate = useNavigate();
  const clients = user?.accessible_clients || [];

  // Auto-redirect single-client viewers straight to the client detail (overview tab).
  useEffect(() => {
    if (clients.length === 1) {
      navigate(`/clients/${clients[0].client_id}`, { replace: true });
    }
  }, [clients.length, clients, navigate]);

  if (!clients.length) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-400" />
          <h2 className="text-xl font-bold mb-2">Nessun cliente assegnato</h2>
          <p className="text-slate-400 text-sm mb-6">
            Il tuo account non ha ancora accesso a nessun cliente. Contatta il tuo commercialista per attivare l'accesso.
          </p>
          <Button variant="outline" onClick={onLogout} className="bg-transparent border-slate-700 text-white hover:bg-slate-800">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </div>
    );
  }

  // Single client: redirecting (effect runs)
  if (clients.length === 1) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento...</div>;
  }

  // Multi-client: simple selector
  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <header className="px-5 pt-6 pb-4 bg-white border-b">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Portale Cliente</p>
            <h1 className="text-xl font-bold leading-tight">Ciao {user?.name?.split(" ")[0] || "Cliente"}</h1>
          </div>
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="viewer-logout-btn">
            <LogOut className="h-4 w-4 mr-1" /> Esci
          </Button>
        </div>
      </header>
      <main className="p-4 space-y-3 max-w-2xl mx-auto">
        <p className="text-sm text-slate-500 mt-2">Seleziona la tua attività:</p>
        {clients.map((c) => (
          <Card
            key={c.client_id}
            className="cursor-pointer hover:border-blue-400 transition-colors active:bg-slate-100"
            onClick={() => navigate(`/clients/${c.client_id}`)}
            data-testid={`viewer-client-card-${c.client_id}`}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-base leading-tight truncate">{c.name}</h2>
                <p className="text-xs text-slate-500 truncate">{c.sector}</p>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400 shrink-0" />
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
